import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotWorkflowClient } from '@frontend/slot/api';
import { TransactionActorClient, TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    error: ReactNode | null;
    resetError: () => void;
}

export const useCreateRecycleTransaction = (): ViewProps => {
    const { store, navigationState, wizardData, onSubmit } = useScreen();
    const [error, setError] = useState<ReactNode | null>(null);
    const hasStartedRef = useRef<boolean>(false);

    const selectedProduct = wizardData.selectedProduct as IProduct | string | undefined;
    const selectedProductId = typeof selectedProduct === 'string' ? selectedProduct : selectedProduct?.id;

    const submitStep = useMemo(
        () => store?.steps.find((step) => step.screen === ScreenType.CREATE_RECYCLE_TRANSACTION && step.trigger === ScreenTrigger.SUBMIT),
        [store?.steps]
    );

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id
    });
    const packageContext = useWizardPackage({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });

    const workflowInfo = useMemo(() => {
        const settings = store?.functionalitySettings;
        if (!settings) return null;
        const transactionWf = settings.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value as string;
        const dropWf = settings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value as string;
        const pickWf = settings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
        if (!transactionWf || !dropWf) return null;
        return { transactionWf, dropWf, pickWf };
    }, [store?.functionalitySettings]);

    const createRecycleTransaction = useCallback(async () => {
        if (!submitStep) {
            setError(CommonMessage.WIZARD.RECYCLE.CREATE_TRANSACTION.NO_SUBMIT_STEP);
            return;
        }
        if (!selectedProductId) {
            setError(CommonMessage.WIZARD.RECYCLE.CREATE_TRANSACTION.NO_PRODUCT_SELECTED);
            return;
        }
        if (!workflowInfo) {
            setError(CommonMessage.WIZARD.RECYCLE.CREATE_TRANSACTION.MISSING_WORKFLOW_SETTINGS);
            return;
        }
        if (!navigationState.iot?.account_id || !navigationState.iot.spot_id || !store?.wizard || !store.functionality) {
            setError(CommonMessage.WIZARD.RECYCLE.CREATE_TRANSACTION.MISSING_KIOSK_CONTEXT);
            return;
        }

        const accountId = navigationState.iot.account_id;
        const spotId = navigationState.iot.spot_id;

        try {
            const slotsResult = await SlotWorkflowClient.postAvailableSlots(accountId, spotId, {
                account_id: accountId,
                application: store.functionality.application,
                feature: Feature.RECYCLE_TRANSACTION,
                functionality_id: store.functionality.id,
                product_id: selectedProductId,
                transaction_type: TransactionType.VENDING
            });
            if (slotsResult.results.length === 0) {
                setError(CommonMessage.WIZARD.RECYCLE.CREATE_TRANSACTION.NO_AVAILABLE_SLOTS);
                return;
            }
            const availableSlot = slotsResult.results[0];

            const transaction = await TransactionClient.postTransaction(accountId, {
                type: TransactionType.VENDING,
                workflow_id: workflowInfo.transactionWf,
                spot_id: spotId,
                module_id: availableSlot.module_id,
                slot_id: availableSlot.id,
                user_id: navigationState.user?.id,
                iot_id: navigationState.iot.id,
                application: store.functionality.application,
                feature: Feature.RECYCLE_TRANSACTION,
                state: TransactionStateName.TRANSACTION_CREATED
            });

            await transactionContext.localUpdateTransaction({
                id: transaction.id,
                state: transaction.state,
                workflow_id: transaction.workflow_id,
                type: transaction.type,
                user_id: transaction.user_id,
                spot_id: transaction.spot_id,
                module_id: transaction.module_id,
                slot_id: transaction.slot_id,
                iot_id: transaction.iot_id,
                application: transaction.application,
                feature: transaction.feature
            });

            if (navigationState.user?.id) {
                await TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                    type: TransactionActorType.DROPPED_OFF_BY,
                    user_id: navigationState.user.id
                });
            }

            const dropPkg = await PackageClient.postPackage(transaction.account_id, transaction.id, {
                state: PackageStateName.PACKAGE_CREATED,
                workflow_id: workflowInfo.dropWf,
                user_id: transaction.user_id,
                quantity: 1,
                type: PackageType.DROP_OFF,
                spot_id: availableSlot.spot_id,
                module_id: availableSlot.module_id,
                slot_id: availableSlot.id,
                product_id: selectedProductId,
                application: Application.VENDING,
                feature: Feature.RECYCLE_TRANSACTION
            });
            await packageContext.localUpdatePackage(dropPkg);

            if (workflowInfo.pickWf) {
                await PackageClient.postPackage(transaction.account_id, transaction.id, {
                    state: PackageStateName.PACKAGE_CREATED,
                    workflow_id: workflowInfo.pickWf,
                    user_id: transaction.user_id,
                    quantity: 1,
                    type: PackageType.AUDIT,
                    spot_id: availableSlot.spot_id,
                    module_id: availableSlot.module_id,
                    slot_id: availableSlot.id,
                    product_id: selectedProductId,
                    dependent_package_id: dropPkg.id,
                    application: Application.VENDING,
                    feature: Feature.RECYCLE_TRANSACTION
                });
            }

            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });

            onSubmit(submitStep);
        } catch (e) {
            Logger.error('Failed to create recycle transaction', e);
            setError(CommonMessage.WIZARD.RECYCLE.CREATE_TRANSACTION.FAILED);
        }
    }, [navigationState.iot, navigationState.user?.id, onSubmit, packageContext, selectedProductId, store, submitStep, transactionContext, workflowInfo]);

    useEffect(() => {
        if (hasStartedRef.current) return;
        hasStartedRef.current = true;
        createRecycleTransaction();
    }, [createRecycleTransaction]);

    return {
        error,
        resetError: useCallback(() => setError(null), [])
    };
};
